// Project portfolio data for rendering project cards and modals
const projectData = [
  {
    title: "Pristine Smiles Website",
    blurb: "Responsive dental clinic site featuring booking, pricing, and secure payments.",
    description: `Led full-stack development of a rebranded dental clinic website using React.js and Node.js. Integrated Leaflet for maps, Stripe for secure payments, and Calendly for bookings. Delivered a multi-page, user-friendly site with dynamic content, email notifications, and a secure backend with rate limiting.`,
    stack: ["React", "JavaScript", "Node.js", "Express.js", "Bootstrap CSS", "Leaflet", "Stripe API", "Calendly API"],
    role: "Full-Stack Developer",
    duration: "Jul 2025 – Present",
    challenges: [
      "Seamlessly integrating third-party services (Calendly, Stripe)",
      "Designing a responsive UI that aligns with client branding",
      "Adapting quickly to evolving client requirements"
    ],
    features: [
      "Landing page with hero video and transformation galleries",
      "Multi-column services and payment dropdown menus",
      "Stripe integration for secure payments",
      "Calendly booking for special offers",
      "Pricing page with email and download options",
      "Validated contact form with email notifications",
      "Backend API managing emails, security, and rate limiting"
    ],
    imageUrls: [
      "/project_images/pristinesmiles/pristine1.png",
      "/project_images/pristinesmiles/pristine2.png",
      "/project_images/pristinesmiles/pristine3.png",
      "/project_images/pristinesmiles/pristine4.png",
      "/project_images/pristinesmiles/pristine5.png",
      "/project_images/pristinesmiles/pristine6.png",
      "/project_images/pristinesmiles/pristine7.png",
      "/project_images/pristinesmiles/pristine8.png"
    ],
    github: "",
    live: "https://www.mypristinesmile.com/",
    relatedPages: [
      {
        title: "Veneers At Smile Craft Landing Page",
        github: "https://github.com/sakshamlama1/SmileCraftDental-LandingPage",
        live: "https://veneersatsmilecraft.com",
        imageUrls: ["/project_images/smilecraftdental/veneers1.png"]
      }
    ]
  },
  {
    title: "Curriculum Mapping Tool",
    blurb: "Web app automating curriculum visualization through dynamic Excel data mapping.",
    description: `Developed during a consulting internship at the University of Sydney, this tool streamlines curriculum mapping by connecting unit learning outcomes to accreditation competencies. Led front-end development using React and Bootstrap, creating a responsive, user-friendly interface that supports Excel file uploads and interactive data visualizations. Worked closely with backend developers to integrate APIs, ensuring cross-browser compatibility and optimized performance.`,
    stack: ["React", "JavaScript", "Bootstrap"],
    role: "Front-End Lead Developer",
    duration: "Dec 2022 – Dec 2024",
    challenges: [
      "Designing an intuitive UI accessible to both technical and non-technical users",
      "Managing dynamic Excel uploads and in-browser data visualization",
      "Ensuring responsive design and consistent cross-browser support",
      "Incorporating stakeholder feedback while maintaining performance"
    ],
    features: [
      "Interactive curriculum mapping visualizations with Excel input",
      "Dynamic tables linking unit and program learning outcomes",
      "Support for uploading and updating program and keyword Excel files",
      "Real-time user feedback and clear, accessible navigation",
      "Collaboration tools and data export functionality"
    ],
    imageUrls: [
      "/project_images/curriculummappingtool/mappingtool1.png",
      "/project_images/curriculummappingtool/mappingtool2.png",
      "/project_images/curriculummappingtool/mappingtool3.png",
      "/project_images/curriculummappingtool/mappingtool4.png",
    ],
    confidential: "This project is proprietary to the University of Sydney and not publicly available. Screenshots and detailed descriptions showcase its features and my contributions.",
  },
  {
    title: "Mature Missions Website",
    blurb: "Responsive elderly care platform with tailored dashboards and secure payments.",
    description: `Developed as a university project, this platform connects elderly users with caregivers for personal care and mobility support. Led front-end design and development using React and Bootstrap, ensuring accessibility and responsiveness. Integrated with a Java Spring Boot backend via REST APIs. Implemented subscription management with Stripe and notification systems.`,
    stack: ["React", "JavaScript", "Bootstrap", "REST APIs", "Stripe API", "Swagger", "Figma"],
    role: "Front-End Lead Developer",
    duration: "Aug 2023 – Nov 2023",
    challenges: [
      "Designing an accessible UI for elderly users",
      "Integrating complex backend APIs seamlessly",
      "Implementing secure and smooth Stripe payment processing",
      "Meeting tight deadlines coordinating with backend team"
    ],
    features: [
      "Role-specific dashboards for users, caregivers, and admins",
      "Subscription selection with secure Stripe payments", 
      "Service request lifecycle management (create, accept, cancel, rate)",
      "Profile management with editable details and notifications",
      "Email/SMS notifications for important updates",
      "Admin features for user and payment management",
      "Figma prototypes guiding responsive UI development",
      "Cross-browser/device compatibility testing"
    ],
    imageUrls: [
      "/project_images/maturemissions/maturemissions1.png",
      "/project_images/maturemissions/maturemissions2.png",
      "/project_images/maturemissions/maturemissions3.png",
      "/project_images/maturemissions/maturemissions4.png",
      "/project_images/maturemissions/maturemissions5.png",
      "/project_images/maturemissions/maturemissions6.png",
      "/project_images/maturemissions/maturemissions7.png",
      "/project_images/maturemissions/maturemissions8.png",
      "/project_images/maturemissions/maturemissions9.png",
      "/project_images/maturemissions/maturemissions10.png",
      "/project_images/maturemissions/maturemissions11.png",
      "/project_images/maturemissions/maturemissions12.png",
      "/project_images/maturemissions/maturemissions13.png",
      "/project_images/maturemissions/maturemissions14.png"
    ],
    github: "https://github.com/sakshamlama1/Mature-Missions-Website",
    live: ""
  },
  {
    title: "Blockchain-Powered Identity Management Application",
    blurb: "Secure, user-centric identity management leveraging blockchain technology.",
    description: `Developed a full-stack identity management application using React, Node.js, and the Hyperledger Indy framework. The platform provides decentralized, secure identity verification and management. Delivered a seminar on security and user focus, receiving positive feedback.`,
    stack: ["React", "Node.js", "Docker", "MySQL", "MySQL Workbench", "Hyperledger Indy SDK"],
    role: "Full-Stack Developer",
    duration: "Aug 2023 – May 2024",
    challenges: [
      "Designing a decentralized identity system enhancing security",
      "Integrating Hyperledger Indy SDK with backend and frontend",
      "Setting up Docker environments and managing MySQL databases",
      "Ensuring a seamless user experience despite technical complexity"
    ],
    features: [
      "Decentralized identity management with blockchain security",
      "Secure user registration, login, and profile management",
      "Comprehensive transaction tracking and dashboard",
      "Settings and support pages for customization",
      "Responsive UI across key pages",
      "Docker-based setup for streamlined development",
      "Robust SQL database integration"
    ],
    imageUrls: [
      "/project_images/blockchain/blockchain-id1.png",
      "/project_images/blockchain/blockchain-id2.png",
      "/project_images/blockchain/blockchain-id3.png",
      "/project_images/blockchain/blockchain-id4.png",
      "/project_images/blockchain/blockchain-id5.png",
      "/project_images/blockchain/blockchain-id6.png",
      "/project_images/blockchain/blockchain-id7.png",
      "/project_images/blockchain/blockchain-id8.png"
    ],
    github: "https://github.com/sakshamlama1/Decentralized-Blockchain-IDM",
    live: ""
  }
];

export default projectData;
